import { useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Check, FileText, Loader2, UploadCloud, X } from "lucide-react";
import { cn } from "@/utils";

interface FileDropzoneProps {
  file: File | null;
  /** True while the file is being processed (e.g. AI resume parsing). */
  parsing?: boolean;
  /** True once processing has succeeded. */
  parsed?: boolean;
  accept?: string;
  /** Helper line under the idle prompt. */
  hint?: string;
  onSelect: (file: File) => void;
  onRemove: () => void;
  parsingLabel?: string;
  parsedTitle?: string;
  /** Extra success content rendered under the parsed-state filename. */
  parsedNote?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * Drag-and-drop upload surface with three visual states — idle, processing,
 * and success. Used as the hero moment of the job application (resume parse),
 * but generic enough for any single-file upload.
 */
export function FileDropzone({
  file,
  parsing = false,
  parsed = false,
  accept,
  hint,
  onSelect,
  onRemove,
  parsingLabel = "Processing…",
  parsedTitle = "File uploaded",
  parsedNote,
  disabled,
  className,
}: FileDropzoneProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const open = () => {
    if (disabled || parsing) return;
    inputRef.current?.click();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || parsing) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) onSelect(dropped);
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelect(f);
          // allow re-selecting the same file
          e.target.value = "";
        }}
      />

      {/* ── Processing ── */}
      {parsing ? (
        <div
          className="relative overflow-hidden rounded-2xl border-2 border-dashed p-9 text-center"
          style={{
            borderColor: "rgba(var(--acc2-rgb),0.4)",
            background:
              "linear-gradient(180deg, rgba(var(--acc2-rgb),0.05), transparent)",
          }}
        >
          <div className="shimmer pointer-events-none absolute inset-0" aria-hidden />
          <div className="relative flex flex-col items-center gap-4">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full"
              style={{
                background: "rgba(var(--acc2-rgb),0.12)",
                border: "1px solid rgba(var(--acc2-rgb),0.3)",
              }}
            >
              <Loader2 className="h-6 w-6 animate-spin text-[var(--acc2)]" />
            </div>
            <div>
              <p
                className="text-sm font-semibold"
                style={{ color: "var(--landing-text)" }}
              >
                {parsingLabel}
              </p>
              <p
                className="mt-1 text-xs"
                style={{ color: "var(--landing-text-muted)" }}
              >
                {file?.name}
              </p>
            </div>
          </div>
        </div>
      ) : parsed && file ? (
        /* ── Success ── */
        <div
          className="rounded-2xl border p-6"
          style={{
            borderColor: "rgba(var(--acc2-rgb),0.3)",
            background: "rgba(var(--acc2-rgb),0.04)",
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl"
              style={{
                background:
                  "linear-gradient(135deg, rgba(var(--acc2-rgb),0.2), rgba(var(--pos-rgb),0.2))",
                border: "1px solid rgba(var(--acc2-rgb),0.3)",
              }}
            >
              <Check className="h-6 w-6 text-[var(--acc2)]" />
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-sm font-semibold"
                style={{ color: "var(--landing-text)" }}
              >
                {parsedTitle}
              </p>
              <p
                className="mt-0.5 flex items-center gap-1.5 truncate text-xs"
                style={{ color: "var(--landing-text-muted)" }}
              >
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{file.name}</span>
                <span className="shrink-0">
                  · {(file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </p>
              {parsedNote && <div className="mt-3">{parsedNote}</div>}
            </div>
            <button
              type="button"
              onClick={onRemove}
              className="shrink-0 rounded-lg p-1.5 transition-colors hover:bg-destructive/10 hover:text-destructive"
              style={{ color: "var(--landing-text-dim)" }}
              aria-label="Remove file"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        /* ── Idle ── */
        <button
          type="button"
          onClick={open}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          disabled={disabled}
          className={cn(
            "group flex w-full flex-col items-center gap-4 rounded-2xl border-2 border-dashed p-9 text-center transition-all",
            dragging
              ? "scale-[1.01] border-[var(--acc2)]"
              : "hover:border-[var(--acc2)]/50",
            disabled && "cursor-not-allowed opacity-60",
          )}
          style={{
            borderColor: dragging
              ? "var(--acc2)"
              : "var(--landing-glass-border)",
            background: dragging
              ? "rgba(var(--acc2-rgb),0.05)"
              : "var(--landing-surface)",
          }}
        >
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full transition-transform group-hover:-translate-y-0.5"
            style={{
              background: "rgba(var(--acc2-rgb),0.08)",
              border: "1px solid rgba(var(--acc2-rgb),0.18)",
            }}
          >
            <UploadCloud className="h-6 w-6 text-[var(--acc2)]" />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
              <span className="text-[var(--acc2)]">Click to upload</span>
              <span style={{ color: "var(--landing-text-muted)" }}>
                {" "}
                or drag &amp; drop
              </span>
            </p>
            {hint && (
              <p
                className="mt-1.5 text-xs"
                style={{ color: "var(--landing-text-dim)" }}
              >
                {hint}
              </p>
            )}
          </div>
        </button>
      )}
    </div>
  );
}
